import { InputAdornment, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useRef } from "react";

export interface EditTemplatesProps {
  templateName: string;
  prompt: string;
  onTemplateNameChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  namePlaceholder?: string;
  promptPlaceholder?: string;
  promptRows?: number;
  showGuidance?: boolean;
  footer?: React.ReactNode;
  nameActions?: React.ReactNode;
}

/** Shared template name and extraction prompt fields. */
const EditTemplates: React.FC<EditTemplatesProps> = ({
  templateName,
  prompt,
  onTemplateNameChange,
  onPromptChange,
  disabled = false,
  readOnly = false,
  namePlaceholder = "e.g. Customer success review",
  promptPlaceholder = "Describe exactly what should be extracted, how it should be grouped, and the evidence required...",
  promptRows = 8,
  showGuidance = false,
  footer,
  nameActions,
}) => {
  const templateNameInputRef = useRef<HTMLInputElement>(null);
  const extractionPromptInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (disabled || readOnly) return;

    if (showGuidance) {
      templateNameInputRef.current?.focus();
    } else {
      extractionPromptInputRef.current?.focus();
    }
  }, [showGuidance, disabled, readOnly]);

  return (
  <Box className={`template-edit-fields-layout ${showGuidance ? "with-guidance" : ""}`}>
    {showGuidance && (
      <Box className="template-edit-guidance">
        <Typography className="template-edit-guidance-title">WRITING A STRONG PROMPT</Typography>
        {[
          ["Name the outcome", "Specify what information or insights you want to extract from the meeting."],
          ["List the details", "Specify the key details your template should capture, such as candidate skills, experience, and suitability for an interview analysis."],
          ["Define the format", "Describe how you'd like the extracted information to be structured and summarized."],
        ].map(([heading, body], index) => (
          <Box className="template-edit-guidance-step" key={heading}>
            <Box className="template-edit-step-number">{index + 1}</Box>
            <Box>
              <Typography>{heading}</Typography>
              <Typography component="p">{body}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    )}
    <Box className="template-edit-fields">
    <Box className="template-edit-field-group">
      <Box className="template-edit-name-row">
        <Typography className="template-edit-field-label">Template name</Typography>
        {nameActions}
      </Box>
      <TextField
        fullWidth
        size="small"
        inputRef={templateNameInputRef}
        value={templateName}
        onChange={(event) => {
          const value = event.target.value;

          onTemplateNameChange(
            value.charAt(0).toUpperCase() + value.slice(1)
          );
        }}
        placeholder={namePlaceholder}
        className={`template-edit-input ${readOnly ? "is-readonly" : ""}`}
        variant="outlined"
        disabled={disabled}
        inputProps={{ maxLength: 25 }}
        InputProps={{
          readOnly,
          endAdornment: (
            <InputAdornment position="end" disablePointerEvents>
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  color: templateName.length >= 25 ? "#D92D20" : "#8e8e9f",
                }}
              >
                {templateName.length}/25
              </Typography>
            </InputAdornment>
          ),
        }}
      />
    </Box>

    <Box className="template-edit-field-group">
      <Box className="template-edit-prompt-header">
        <Box>
          <Typography className="template-edit-field-label">
            Extraction prompt
          </Typography>
          <Typography className="template-edit-field-sublabel">
            Specify exactly what should be extracted from the recording.
          </Typography>
        </Box>
      </Box>
      <TextField
        fullWidth
        multiline
        inputRef={extractionPromptInputRef}
        {...(showGuidance
          ? { minRows: Math.min(promptRows, 4) }
          : { rows: promptRows })}
        value={prompt}
        onChange={(event) => {
          const value = event.target.value;

          onPromptChange(
            value.charAt(0).toUpperCase() + value.slice(1)
          );
        }}
        placeholder={promptPlaceholder}
        className={`template-edit-textarea ${readOnly ? "is-readonly" : ""}`}
        variant="outlined"
        disabled={disabled}
        InputProps={{ readOnly }}
        autoFocus={!showGuidance}
      />
    </Box>
    {footer && <Box className="template-edit-footer">{footer}</Box>}
    </Box>
  </Box>
  );
};

export default EditTemplates;
